import React, { useState, useEffect } from 'react'
import { Card, List, Button, Form, Badge, Modal, Tabs, Empty, Space, theme, Skeleton } from 'antd'
import { PlusOutlined, ExclamationCircleOutlined, CheckCircleOutlined, ReloadOutlined } from '@ant-design/icons'
import TaskItem from './components/TaskItem'
import TaskFilters from './components/TaskFilters'
import CreateTaskModal from './components/CreateTaskModal'
import EditTaskModal from './components/EditTaskModal'
import PreAdmissionModal from '../PreAdmissionModal'
import useTasks from './hooks/useTasks'
import useTaskFilters from './hooks/useTaskFilters'
import { useNotifications } from '../../context/NotificationsContext'
import { useWebSocket } from '../../context/WebSocketContext'
import { CURRENT_USER, DATE_FORMAT } from './config/constants'
import { STATUS } from './config/taskStatus'
import styles from './styles/Tasks.module.css'
import AgiliteSkeleton from '../../../../../reusable-components/AgiliteSkeleton'
import { hexToRGBA } from '../../../../../lib/utils'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSync } from '@fortawesome/free-solid-svg-icons'
import dayjs from 'dayjs'
import { useSelector } from 'react-redux'

const { TabPane } = Tabs
const { confirm } = Modal

const CustomEmpty = ({ isCompleted }) => (
  <Empty
    image={null}
    description={
      <div className={styles.emptyState}>
        <CheckCircleOutlined className={styles.emptyIcon} />
        <span>
          {isCompleted
            ? 'No completed tasks yet. Tasks marked as done will appear here."'
            : "No active tasks. Click 'New Task' to create one!"}
        </span>
      </div>
    }
  />
)

const TaskSkeleton = () => (
  <List.Item className={`${styles.taskItem} ${styles.pending}`} style={{ padding: '16px 20px' }}>
    <div className={styles.taskHeaderRight} style={{ marginBottom: 12 }}>
      <Skeleton.Button active size='small' style={{ width: 70, height: 24, borderRadius: '12px' }} />
    </div>
    <div className={styles.taskContent}>
      <div className={styles.taskMain}>
        <div className={styles.taskTop}>
          <div className={styles.taskDescription}>
            <Skeleton.Input active block style={{ width: '70%', height: 24 }} />
          </div>
        </div>
        <div className={styles.taskHeader} style={{ marginTop: '12px' }}>
          <div className={styles.taskHeaderLeft}>
            <Skeleton.Button active style={{ width: 100, height: 24, borderRadius: '4px' }} />
            <Skeleton.Button active style={{ width: 80, height: 24, borderRadius: '12px', marginLeft: '16px' }} />
          </div>
        </div>
        <div className={styles.taskMeta} style={{ marginTop: '12px' }}>
          <div className={styles.taskMetaGroup}>
            <Skeleton.Input active style={{ width: 140, height: 16 }} />
            <Skeleton.Input active style={{ width: 200, height: 16, marginLeft: '16px' }} />
          </div>
        </div>
      </div>
    </div>
    <div className={styles.taskRightSide}>
      <Space size='middle'>
        <Skeleton.Button active style={{ width: 100, height: 32, borderRadius: '4px' }} />
        <Skeleton.Button active style={{ width: 70, height: 32, borderRadius: '4px' }} />
        <Skeleton.Button active style={{ width: 32, height: 32, borderRadius: '4px' }} />
      </Space>
    </div>
  </List.Item>
)

function Tasks() {
  const {
    tasks,
    medicalProfs,
    loadingTasks,
    isCreateModalVisible,
    setIsCreateModalVisible,
    handleCreateTask,
    handleCompleteTask,
    handleUpdateTask,
    handleDeleteTask,
    handleFetchTasks
  } = useTasks()
  const { token } = theme.useToken()
  const [activeTab, setActiveTab] = useState('active')
  const [isEditModalVisible, setIsEditModalVisible] = useState(false)
  const [selectedTask, setSelectedTask] = useState(null)
  const [isPreAdmissionModalVisible, setIsPreAdmissionModalVisible] = useState(false)
  const [selectedPatient, setSelectedPatient] = useState(null)
  const { addNotification } = useNotifications()
  const { isConnected } = useWebSocket()
  const [createForm] = Form.useForm()
  const [editForm] = Form.useForm()
  const authState = useSelector((state) => state.auth)
  const { filters, handleFilterChange, filteredTasks } = useTaskFilters(tasks)

  // Ensure filters are set to default values when component mounts
  useEffect(() => {
    handleFilterChange('assignee', 'all')
    handleFilterChange('taskType', 'all')
    handleFilterChange('status', 'all')
  }, [handleFilterChange])

  // Filter active and completed tasks, then sort by due date (soonest first)
  const sortTasksByDueDate = (tasksArray) => {
    return [...tasksArray].sort((a, b) => {
      return dayjs(a.dueDate).diff(dayjs(b.dueDate))
    })
  }

  const isTaskOverdue = (task) => {
    return task.status === STATUS.ACTIVE && dayjs().isAfter(dayjs(task.dueDate), 'day')
  }

  const activeTasks = sortTasksByDueDate(filteredTasks.filter((task) => task.status === STATUS.ACTIVE))

  const completedTasks = sortTasksByDueDate(filteredTasks.filter((task) => task.status === STATUS.COMPLETED))

  // Calculate count of overdue tasks
  const overdueTasksCount = activeTasks.filter(isTaskOverdue).length

  const handleEditTask = (values) => {
    if (selectedTask) {
      handleUpdateTask(selectedTask._id, values)
      setIsEditModalVisible(false)
    }
  }

  const handleEditClick = (task) => {
    setSelectedTask(task)
    setIsEditModalVisible(true)

    // Populate the form with existing task data
    editForm.setFieldsValue({
      description: task.description,
      taskType: task.taskType,
      assignee: task.assignee,
      priority: task.priority,
      dueDate: task.dueDate ? dayjs(task.dueDate) : null
    })
  }

  const handleDeleteClick = (taskId) => {
    confirm({
      title: 'Are you sure you want to delete this task?',
      icon: <ExclamationCircleOutlined />,
      content: 'This action cannot be undone.',
      okText: 'Yes, delete',
      okType: 'danger',
      cancelText: 'No, cancel',
      onOk() {
        handleDeleteTask(taskId)
      }
    })
  }

  const renderTaskList = (tasks, isCompleted = false) => (
    <>
      {loadingTasks ? (
        <TaskSkeleton />
      ) : (
        <List
          className={styles.tasksList}
          size='small'
          dataSource={tasks}
          locale={{ emptyText: <CustomEmpty isCompleted={isCompleted} /> }}
          renderItem={(task) => (
            <TaskItem
              key={task.id}
              task={task}
              isOverdue={isTaskOverdue(task)}
              onComplete={handleCompleteTask}
              onDelete={handleDeleteClick}
              onEdit={handleEditClick}
              canDelete={task.createdBy === 'You' || task.createdBy === CURRENT_USER}
            />
          )}
        />
      )}
    </>
  )

  return (
    <Card
      title={
        <div className={styles.cardHeader}>
          <div className={styles.cardTitle}>
            <span>Tasks</span>
            {!isConnected && <Badge status='error' text='Offline' style={{ marginLeft: 8, fontSize: 12 }} />}
          </div>
          <Button
            type='primary'
            icon={<PlusOutlined />}
            onClick={() => setIsCreateModalVisible(true)}
            disabled={!isConnected}
          >
            New Task
          </Button>
        </div>
      }
      className={styles.miniTasksCard}
    >
      <TaskFilters medicalProfs={medicalProfs} filters={filters} onFilterChange={handleFilterChange} />

      <Tabs defaultActiveKey='active'>
        <TabPane
          tab={
            <span className={styles.tabWithBadge}>
              Active
              <Badge
                count={activeTasks.length}
                style={{
                  backgroundColor: '#e13f56',
                  marginLeft: '8px',
                  lineHeight: '20px',
                  height: '20px',
                  minWidth: '20px',
                  fontSize: '12px',
                  padding: '0 6px',
                  textAlign: 'center',
                  borderRadius: '10px'
                }}
              />
            </span>
          }
          key='active'
        >
          {renderTaskList(activeTasks, false)}
        </TabPane>
        <TabPane
          tab={
            <span className={styles.tabWithBadge}>
              Completed
              <Badge
                count={completedTasks.length}
                style={{
                  backgroundColor: '#8c8c8c',
                  marginLeft: '8px',
                  lineHeight: '20px',
                  height: '20px',
                  minWidth: '20px',
                  fontSize: '12px',
                  padding: '0 6px',
                  textAlign: 'center',
                  borderRadius: '10px'
                }}
              />
            </span>
          }
          key='completed'
        >
          {renderTaskList(completedTasks, true)}
        </TabPane>
      </Tabs>

      <CreateTaskModal
        medicalProfs={medicalProfs}
        visible={isCreateModalVisible}
        onCancel={() => setIsCreateModalVisible(false)}
        onSubmit={handleCreateTask}
        form={createForm}
      />

      <EditTaskModal
        visible={isEditModalVisible}
        onCancel={() => {
          setIsEditModalVisible(false)
          setSelectedTask(null)
          editForm.resetFields()
        }}
        onSubmit={handleEditTask}
        form={editForm}
        medicalProfs={medicalProfs}
      />
    </Card>
  )
}

export default Tasks
